import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useWeb3Auth } from "@web3auth/modal-react-hooks";
import { getPublicCompressed } from "@toruslabs/eccrypto";
import { useBotWallets } from "./useBotWallets";
import EthereumRPC from "../auth/rpc/ethersRPC";
import { IProvider } from "@web3auth/base";
import { useBotWalletDeleteMutation } from "@/api/bot/bot-wallet-delete-api";
import { useBotWalletGenerateMutation } from "@/api/bot/bot-wallet-generate-api";
import { useBotWalletImportMutation } from "@/api/bot/bot-wallet-import-api";
import {
  ApiSupportedChain,
  BotWalletSupportedChain,
  WalletType,
} from "./types";
import { resolveBotWalletSwitchChainId } from "./utils";
import { useGetBotUserInfo } from "@/api/bot/bot-get-user-info";
import { useBotWalletSetActiveMutation } from "@/api/bot/bot-wallet-set-active-api";
import toast from "react-hot-toast";
import { useAddUserWallet, useDeleteUserWallet } from "@/api/wallet-management";
import {
  getReferrer,
  hasReferrer,
  removeReferrer,
} from "@/helpers/localStorage";
import { useBotSignupMutation } from "@/api/bot/bot-signup";

const WalletContext = createContext({});

const WALLET_ADDRESSES: Record<
  WalletType,
  {
    address: string;
    botChainId: BotWalletSupportedChain;
    chain: string;
    botWalletIndex: 0 | 1 | 2;
    walletType: WalletType;
  }
> = {
  [WalletType.ETH]: {
    address: "0x",
    botChainId: BotWalletSupportedChain.ETH,
    chain: ApiSupportedChain.ETH,
    botWalletIndex: 0,
    walletType: WalletType.ETH,
  },
  [WalletType.SOL]: {
    address: "",
    botChainId: BotWalletSupportedChain.SOL,
    chain: ApiSupportedChain.SOL,
    botWalletIndex: 0,
    walletType: WalletType.SOL,
  },
};

export type Wallet = (typeof WALLET_ADDRESSES)[keyof typeof WALLET_ADDRESSES];

type WalletContextType = {
  currentWallet: Wallet;
  setWallet: (wallet: Wallet) => void;
  ethereumAddresses: Wallet[];
  solanaAddresses: Wallet[];
  appPubKey: string;
  userId: string;
  tokenScreenerWallet: WalletType[];
  setTokenScreenerWallet: Dispatch<SetStateAction<WalletType[]>>;
  onWalletDelete: (address: string, walletType: WalletType) => void;
  onWalletCreate: (walletType: WalletType) => void;
  onImportWallet: (privateKey: string, walletType: WalletType) => void;
};

const WalletProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { web3Auth, isConnected } = useWeb3Auth();
  const [currentWallet, setCurrentWallet] = useState<Wallet | null>(null);
  const [tokenScreenerWallet, setTokenScreenerWallet] = useState<WalletType[]>([
    WalletType.ETH,
    WalletType.SOL,
  ]);
  const [appPubKey, setAppPubKey] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  const hasSignedUp = useRef(false);

  // Multiple wallets as described in the docs
  // https://web3auth.io/docs/guides/pnp-no-modal-multichain?utm_source=chatgpt.com
  const {
    ethersWallets,
    solanaWallets,
    refetch: refetchBotWallets,
  } = useBotWallets();

  const { mutate: mutateBotSignup } = useBotSignupMutation();

  useEffect(() => {
    if (appPubKey && userId && hasReferrer() && !hasSignedUp.current) {
      const referrer = getReferrer()!;
      mutateBotSignup({
        appPubKey,
        userId,
        referrer,
      });
      hasSignedUp.current = true;
      removeReferrer();
    }
  }, [appPubKey, userId, hasSignedUp, mutateBotSignup]);

  const { data: botUserInfo, refetch: refetchBotUserInfo } = useGetBotUserInfo({
    appPubKey,
    userId,
  });

  const { mutateAsync: mutateSetActiveWallet } =
    useBotWalletSetActiveMutation();

  const ethereumAddresses: Wallet[] = useMemo(() => {
    if (!ethersWallets) {
      return [];
    }
    return ethersWallets.map((address, index) => ({
      ...WALLET_ADDRESSES[WalletType.ETH],
      address: address,
      botWalletIndex: index as 0 | 1 | 2,
    }));
  }, [ethersWallets]);

  const solanaAddresses: Wallet[] = useMemo(() => {
    if (!solanaWallets) {
      return [];
    }
    return solanaWallets.map((address, index) => ({
      ...WALLET_ADDRESSES[WalletType.SOL],
      address: address,
      botWalletIndex: index as 0 | 1 | 2,
    }));
  }, [solanaWallets]);

  useEffect(() => {
    if (!botUserInfo) {
      return;
    }

    if (solanaAddresses.length === 0 && ethereumAddresses.length === 0) {
      setCurrentWallet(null);
      return;
    }

    const solanaAddress = solanaAddresses.find(
      (address) => address.address === botUserInfo.currentWallet
    );

    if (solanaAddress) {
      setCurrentWallet(solanaAddress);
      return;
    }

    const ethereumAddress = ethereumAddresses.find(
      (address) => address.address === botUserInfo.currentWallet
    );

    if (ethereumAddress) {
      setCurrentWallet(ethereumAddress);
    }
  }, [botUserInfo, solanaAddresses, ethereumAddresses]);

  const { mutate: deleteWallet, isSuccess: isDeleteWalletSuccess } =
    useBotWalletDeleteMutation();
  const { mutate: deleteApiWallet } = useDeleteUserWallet();

  useEffect(() => {
    if (isDeleteWalletSuccess) {
      refetchBotWallets();
    }
  }, [isDeleteWalletSuccess]);

  const onWalletDelete = (address: string, walletType: WalletType) => {
    const botChainId = resolveBotWalletSwitchChainId(walletType);
    deleteWallet(
      {
        appPubKey,
        userId,
        chainId: botChainId,
        walletAddress: address,
      },
      {
        onSuccess: () => {
          deleteApiWallet({
            address: address,
            chain:
              walletType === WalletType.ETH
                ? ApiSupportedChain.ETH
                : ApiSupportedChain.SOL,
          });
        },
      },
    );
  };

  const { mutate: createWallet, isSuccess: isCreateWalletSuccess } =
    useBotWalletGenerateMutation();
  const { mutate: createApiWallet } = useAddUserWallet();

  const { mutate: mutateImportWallet, isSuccess: isImportWalletSuccess } =
    useBotWalletImportMutation();

  const onImportWallet = (privateKey: string, walletType: WalletType) => {
    const botChainId = resolveBotWalletSwitchChainId(walletType);
    mutateImportWallet(
      {
        appPubKey,
        userId,
        chainId: botChainId,
        privateKey,
      },
      {
        onSuccess: (data) => {
          const onImportWalletSuccess = (walletType: WalletType) => {
            createApiWallet({
              address: data.walletImported.address,
              chain:
                walletType === WalletType.ETH
                  ? ApiSupportedChain.ETH
                  : ApiSupportedChain.SOL,
            });
          };
          onImportWalletSuccess(walletType);
        },
      },
    );
  };

  useEffect(() => {
    if (isCreateWalletSuccess || isImportWalletSuccess) {
      refetchBotWallets();
    }
  }, [isCreateWalletSuccess, isImportWalletSuccess]);

  const onWalletCreate = (walletType: WalletType) => {
    const botChainId = resolveBotWalletSwitchChainId(walletType);
    createWallet(
      {
        appPubKey,
        userId,
        chainId: botChainId,
      },
      {
        onSuccess: (data) => {
          const onCreateWalletSuccess = (walletType: WalletType) => {
            createApiWallet({
              address: data.newWallet.address,
              chain:
                walletType === WalletType.ETH
                  ? ApiSupportedChain.ETH
                  : ApiSupportedChain.SOL,
            });
          };
          onCreateWalletSuccess(walletType);
        },
      },
    );
  };

  const fetchAppPubKey = async () => {
    if (!web3Auth) {
      return;
    }

    try {
      const provider = await web3Auth.connect();
      if (!provider) {
        return;
      }
      const { email, verifierId } = await web3Auth.getUserInfo();
      const ethers = new EthereumRPC(provider as IProvider);
      const privateKey = await ethers.getPrivateKey();
      const formattedPrivateKey = privateKey.padStart(64, "0") ?? "";
      const privateKeyBuffer = Buffer.from(formattedPrivateKey, "hex");
      const appPubKey = getPublicCompressed(privateKeyBuffer).toString("hex");

      setAppPubKey(appPubKey);
      if (email) {
        setUserId(email);
      } else if (verifierId) {
        setUserId(verifierId);
      } else {
        setUserId("");
      }
    } catch (error) {
      await web3Auth.initModal();
      const provider = await web3Auth.connect();
      if (!provider) {
        return;
      }
      const { email, verifierId } = await web3Auth.getUserInfo();
      const ethers = new EthereumRPC(provider as IProvider);
      const privateKey = await ethers.getPrivateKey();
      const formattedPrivateKey = privateKey.padStart(64, "0") ?? "";
      const privateKeyBuffer = Buffer.from(formattedPrivateKey, "hex");
      const appPubKey = getPublicCompressed(privateKeyBuffer).toString("hex");

      setAppPubKey(appPubKey);
      if (email) {
        setUserId(email);
      } else if (verifierId) {
        setUserId(verifierId);
      } else {
        setUserId("");
      }
      console.error("Error fetching app public key:", error);
    }
  };

  useEffect(() => {
    if (!web3Auth || !isConnected) {
      return;
    }

    fetchAppPubKey();
  }, [web3Auth, isConnected]);

  const setWallet = useCallback(
    (wallet: Wallet) => {
      mutateSetActiveWallet({
        appPubKey,
        userId,
        chainId: wallet.botChainId,
        walletAddress: wallet.address,
      })
        .then(() => refetchBotUserInfo())
        .catch(() => toast.error("Failed to change wallet"));
    },
    [appPubKey, userId, refetchBotUserInfo, mutateSetActiveWallet],
  );

  return (
    <WalletContext.Provider
      value={
        {
          currentWallet,
          ethereumAddresses,
          solanaAddresses,
          setWallet,
          appPubKey,
          userId,
          tokenScreenerWallet,
          setTokenScreenerWallet,
          onWalletDelete,
          onWalletCreate,
          onImportWallet,
        } as WalletContextType
      }
    >
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => useContext(WalletContext) as WalletContextType;

export default WalletProvider;
